<template>
  <div class="data-object-management">
    <div class="management-header">
      <h2>Data Object Management</h2>
      <div class="header-actions">
        <el-button type="primary" @click="showCreateDialog">
          <i class="el-icon-plus"></i> Create Data Object
        </el-button>
      </div>
    </div>

    <div class="data-object-list">
      <el-table :data="dataObjects" style="width: 100%" v-loading="loading">
        <el-table-column prop="name" label="Name" min-width="150">
          <template slot-scope="scope">
            <span class="object-name">
              <i :class="scope.row.type === 'ARRAY' ? 'el-icon-files' : 'el-icon-folder'"></i>
              {{ scope.row.name }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="type" label="Type" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.type === 'ARRAY' ? 'warning' : 'primary'" size="small">
              {{ scope.row.type }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="Description" min-width="200" />
        <el-table-column label="Structure" min-width="150">
          <template slot-scope="scope">
            <el-button size="mini" @click="viewStructure(scope.row)">
              View Structure
            </el-button>
          </template>
        </el-table-column>
        <el-table-column label="Actions" width="200" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="editDataObject(scope.row)">
              Edit
            </el-button>
            <el-button size="mini" type="danger" @click="deleteDataObject(scope.row)">
              Delete
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Create/Edit Dialog -->
    <el-dialog 
      :title="editingObject ? 'Edit Data Object' : 'Create Data Object'" 
      :visible.sync="dialogVisible"
      width="90%"
      top="5vh"
    >
      <div class="dialog-content-layout">
        <div class="left-column">
          <el-form :model="currentObject" label-width="120px">
        <el-form-item label="Name" required>
          <el-input v-model="currentObject.name" placeholder="Enter data object name"></el-input>
        </el-form-item>
        
        <el-form-item label="Type" required>
          <el-radio-group v-model="currentObject.type" @change="onTypeChange">
            <el-radio label="OBJECT">Object</el-radio>
            <el-radio label="ARRAY">Array</el-radio>
          </el-radio-group>
        </el-form-item>
        
        <el-form-item label="Description">
          <el-input 
            type="textarea" 
            v-model="currentObject.description" 
            placeholder="Enter description"
            :rows="2"
          ></el-input>
        </el-form-item>
        
        <el-form-item label="Structure">
          <div class="structure-editor">
            <div class="structure-toolbar">
              <el-button 
                size="mini" 
                @click="addRootProperty" 
                :disabled="currentObject.type === 'ARRAY'"
              >
                <i class="el-icon-plus"></i> Add Property
              </el-button>
              <el-button 
                size="mini" 
                @click="expandAll"
              >
                <i class="el-icon-arrow-down"></i> Expand All
              </el-button>
              <el-button 
                size="mini" 
                @click="collapseAll"
              >
                <i class="el-icon-arrow-up"></i> Collapse All
              </el-button>
            </div>
            
            <div class="structure-tree">
              <template v-if="currentObject.type === 'OBJECT'">
                <property-editor
                  v-for="(prop, index) in currentObject.properties"
                  :key="`${prop.id}-${structureKey}`"
                  :property="prop"
                  :index="index"
                  :parent-type="'OBJECT'"
                  :data-objects="dataObjects"
                  @update="updateProperty"
                  @delete="deleteProperty"
                  @add-child="addChildProperty"
                  @import-structure="importPropertyStructure"
                />
              </template>
              
              <div v-if="currentObject.type === 'ARRAY'" class="array-items-editor">
                <div class="array-items-header">
                  <h4>Array Elements:</h4>
                  <p class="array-description">
                    Define multiple element types that can be included in this array. Each element can be a different type (String, Number, Object, etc.).
                  </p>
                </div>
                
                <div class="array-items-list">
                  <div 
                    v-for="(item, index) in currentObject.arrayItems" 
                    :key="`array-item-${index}-${structureKey}`"
                    class="array-item-card"
                  >
                    <div class="array-item-header">
                      <div class="item-info">
                        <span class="item-index">#{{ index + 1 }}</span>
                        <el-input 
                          v-model="item.name" 
                          placeholder="Element name (optional)"
                          size="small"
                          class="item-name"
                          @change="updateArrayItems"
                        />
                        <el-select 
                          v-model="item.type" 
                          size="small" 
                          class="item-type"
                          @change="onArrayItemTypeChange(index)"
                        >
                          <el-option label="String" value="string"></el-option>
                          <el-option label="Number" value="number"></el-option>
                          <el-option label="Boolean" value="boolean"></el-option>
                          <el-option label="Object" value="object"></el-option>
                          <el-option label="Array" value="array"></el-option>
                          <el-option label="Date" value="date"></el-option>
                        </el-select>
                      </div>
                      <div class="item-actions">
                        <el-button 
                          v-if="item.type && item.type.toLowerCase() === 'object'"
                          size="mini" 
                          type="primary"
                          icon="el-icon-download"
                          @click="showArrayItemImportDialog(index)"
                          class="import-from-button"
                          style="margin-right: 8px;"
                        >
                          Import From
                        </el-button>
                        <el-button 
                          size="mini" 
                          type="danger" 
                          @click="removeArrayItem(index)"
                          icon="el-icon-delete"
                        >
                          Delete
                        </el-button>
                      </div>
                    </div>
                    
                    <div class="item-description">
                      <el-input 
                        v-model="item.description" 
                        placeholder="Description for this element type"
                        size="small"
                        @change="updateArrayItems"
                      />
                    </div>
                    
                    <div v-if="item.type && item.type.toLowerCase() === 'object'" class="array-object-properties">
                      <div class="object-properties-header">
                        <h5>Object Properties:</h5>
                      </div>
                      <property-editor
                        v-for="(prop, propIndex) in item.properties"
                        :key="`${prop.id}-${index}-${structureKey}`"
                        :property="prop"
                        :index="propIndex"
                        :parent-type="'OBJECT'"
                        :data-objects="dataObjects"
                        @update="(id, property) => updateArrayItemProperty(index, { id, property })"
                        @delete="(id) => deleteArrayItemProperty(index, id)"
                        @add-child="(parentId) => addArrayItemChildProperty(index, parentId)"
                        @import-structure="(propertyId, dataObjectId) => importArrayItemPropertyStructure(index, propertyId, dataObjectId)"
                      />
                      <el-button size="mini" @click="addArrayItemProperty(index)">
                        <i class="el-icon-plus"></i> Add Property
                      </el-button>
                    </div>
                  </div>
                  
                  <div v-if="!currentObject.arrayItems || currentObject.arrayItems.length === 0" class="no-array-items">
                    <p>No array elements defined. Add elements to specify what types can be included in this array.</p>
                  </div>
                </div>
                
                <div class="array-actions">
                  <el-button size="small" type="primary" @click="addArrayItem">
                    <i class="el-icon-plus"></i> Add Array Element Type
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </el-form-item>
          </el-form>
        </div>
        
        <div class="right-column">
          <div class="structure-preview-section">
            <h3>Data Structure Preview</h3>
            <div class="preview-container">
              <pre class="json-preview">{{ currentStructurePreview }}</pre>
            </div>
          </div>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="saveDataObject">Save</el-button>
      </span>
    </el-dialog>

    <!-- Structure View Dialog -->
    <el-dialog 
      title="Data Object Structure" 
      :visible.sync="structureDialogVisible"
      width="60%"
    >
      <pre class="structure-preview">{{ structurePreview }}</pre>
    </el-dialog>

    <!-- Array Item Import Dialog -->
    <el-dialog 
      title="Import Data Object Structure" 
      :visible.sync="arrayItemImportDialogVisible"
      width="50%"
    >
      <div class="import-dialog-content">
        <p class="import-dialog-description">
          Select a data object to import its structure into this array element:
        </p>
        <el-select 
          v-model="selectedArrayItemImportObject" 
          placeholder="Choose a data object..."
          clearable
          size="medium"
          style="width: 100%;"
          filterable
        >
          <el-option
            v-for="obj in dataObjects.filter(o => o.type === 'OBJECT')"
            :key="obj._id"
            :label="obj.name"
            :value="obj._id"
          >
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <div>
                <div style="font-weight: 500;">{{ obj.name }}</div>
                <div style="font-size: 12px; color: #909399;">{{ obj.description || 'No description' }}</div>
              </div>
              <el-tag size="mini" type="primary">OBJECT</el-tag>
            </div>
          </el-option>
        </el-select>
        <div class="import-warning">
          <i class="el-icon-warning"></i>
          <span>This will replace the current element's property structure.</span>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelArrayItemImport">Cancel</el-button>
        <el-button type="primary" @click="confirmArrayItemImportDialog" :disabled="!selectedArrayItemImportObject">
          Import
        </el-button>
      </span>
    </el-dialog>

  </div>
</template>

<script>
import PropertyEditor from './PropertyEditor.vue'
import { postRequest } from '@/apis/front'

export default {
  name: 'DataObjectManagement',
  components: {
    PropertyEditor
  },
  data() {
    return {
      dataObjects: [],
      loading: false,
      dialogVisible: false,
      structureDialogVisible: false,
      editingObject: null,
      currentObject: {
        name: '',
        type: 'OBJECT',
        description: '',
        properties: [],
        itemType: 'string', // Keep for backward compatibility
        itemProperties: [], // Keep for backward compatibility
        arrayItems: [] // New multi-type array structure
      },
      structurePreview: '',
      propertyIdCounter: 0,
      structureKey: 0,
      selectedArrayImportObject: null,
      selectedArrayItemImportObject: null,
      arrayItemImportDialogVisible: false,
      currentArrayItemIndex: null
    }
  },
  computed: {
    currentStructurePreview() {
      if (!this.currentObject) return '{}'
      
      try {
        const preview = this.buildStructurePreview(this.currentObject)
        return JSON.stringify(preview, null, 2)
      } catch (error) {
        console.error('Error building structure preview:', error)
        return '{}'
      }
    }
  },
  mounted() {
    this.loadDataObjects()
  },
  methods: {
    async loadDataObjects() {
      this.loading = true
      try {
        const response = await postRequest('getMyDataObjects', {
          user_id: this.$store.state.user_id
        })
        if (response.data.success) {
          this.dataObjects = response.data.data || []
        }
      } catch (error) {
        this.$message.error('Failed to load data objects')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    
    showCreateDialog() {
      this.editingObject = null
      this.currentObject = {
        name: '',
        type: 'OBJECT',
        description: '',
        properties: [],
        itemType: 'string', // Keep for backward compatibility
        itemProperties: [], // Keep for backward compatibility
        arrayItems: [] // New multi-type array structure
      }
      this.propertyIdCounter = 0
      this.dialogVisible = true
    },

    
    editDataObject(dataObject) {
      this.editingObject = dataObject
      this.currentObject = JSON.parse(JSON.stringify(dataObject))
      if (!this.currentObject.properties) {
        this.currentObject.properties = []
      }
      if (!this.currentObject.itemProperties) {
        this.currentObject.itemProperties = []
      }
      if (!this.currentObject.arrayItems) {
        this.currentObject.arrayItems = []
        // Migrate from old single itemType structure if present
        if (this.currentObject.type === 'ARRAY' && this.currentObject.itemType) {
          const migratedItem = {
            id: ++this.propertyIdCounter,
            name: `${this.currentObject.itemType} element`,
            type: this.currentObject.itemType,
            description: '',
            properties: this.currentObject.itemType === 'object' ? (this.currentObject.itemProperties || []) : []
          }
          this.currentObject.arrayItems = [migratedItem]
        }
      }
      this.assignPropertyIds(this.currentObject.properties)
      this.assignPropertyIds(this.currentObject.itemProperties)
      this.assignArrayItemIds(this.currentObject.arrayItems)
      this.dialogVisible = true
    },
    
    assignPropertyIds(properties) {
      properties.forEach(prop => {
        if (!prop.id) {
          prop.id = ++this.propertyIdCounter
        }
        if (prop.children) {
          this.assignPropertyIds(prop.children)
        }
      })
    },
    
    assignArrayItemIds(arrayItems) {
      arrayItems.forEach(item => {
        if (!item.id) {
          item.id = ++this.propertyIdCounter
        }
        if (item.properties) {
          this.assignPropertyIds(item.properties)
        }
      })
    },
    
    async saveDataObject() {
      if (!this.currentObject.name) {
        this.$message.error('Name is required')
        return
      }
      
      try {
        const endpoint = this.editingObject ? 'updateDataObject' : 'createDataObject'
        const payload = {
          ...this.currentObject,
          user_id: this.$store.state.user_id
        }
        
        // For update, add the id using _id.$id
        if (this.editingObject) {
          payload.id = (this.editingObject._id && (editingObject._id.$oid || editingObject._id.$id)) || this.editingObject._id
        }
        
        const response = await postRequest(endpoint, payload)
        if (response.data.success) {
          this.$message.success(this.editingObject ? 'Data object updated' : 'Data object created')
          this.dialogVisible = false
          this.loadDataObjects()
        } else {
          this.$message.error(response.data.message || 'Operation failed')
        }
      } catch (error) {
        this.$message.error('Failed to save data object')
        console.error(error)
      }
    },
    
    async deleteDataObject(dataObject) {
      try {
        await this.$confirm('Are you sure you want to delete this data object?', 'Warning', {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        })
        
        const response = await postRequest('deleteDataObject', { 
          id: dataObject._id,
          user_id: this.$store.state.user_id
        })
        
        if (response.data.success) {
          this.$message.success('Data object deleted')
          this.loadDataObjects()
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('Failed to delete data object')
          console.error(error)
        }
      }
    },
    
    viewStructure(dataObject) {
      this.structurePreview = JSON.stringify(
        this.buildStructurePreview(dataObject), 
        null, 
        2
      )
      this.structureDialogVisible = true
    },
    
    buildStructurePreview(dataObject) {
      if (dataObject.type === 'ARRAY') {
        // Use new arrayItems structure if available
        if (dataObject.arrayItems && dataObject.arrayItems.length > 0) {
          // Create two example items for each defined array element type
          const exampleItems = []
          dataObject.arrayItems.forEach(item => {
            if (item.type === 'object' && item.properties) {
              // Add two examples of the object structure
              exampleItems.push(this.buildPropertiesStructure(item.properties))
              exampleItems.push(this.buildPropertiesStructure(item.properties))
            } else {
              // Add two examples of the simple type with restrictions from the item
              exampleItems.push(this.getTypeDisplay(item.type, item.name, item.description, item))
              exampleItems.push(this.getTypeDisplay(item.type, item.name, item.description, item))
            }
          })
          return exampleItems
        }
        // Fallback to old structure for backward compatibility
        if (dataObject.itemType === 'object') {
          // Show two examples to illustrate multiple items
          return [
            this.buildPropertiesStructure(dataObject.itemProperties || []),
            this.buildPropertiesStructure(dataObject.itemProperties || [])
          ]
        }
        return [`<${dataObject.itemType}>`, `<${dataObject.itemType}>`]
      }
      return this.buildPropertiesStructure(dataObject.properties || [])
    },
    
    buildPropertiesStructure(properties) {
      const structure = {}
      properties.forEach(prop => {
        const propName = prop.name || 'unnamed'
        const typeDisplay = this.getTypeDisplay(prop.type, prop.name, prop.description, prop)
        
        if (prop.type === 'object') {
          structure[propName] = this.buildPropertiesStructure(prop.children || [])
        } else if (prop.type === 'array') {
          // Handle new arrayItems structure first
          if (prop.arrayItems && prop.arrayItems.length > 0) {
            // Create two example items for each defined array element type
            const exampleItems = []
            prop.arrayItems.forEach(item => {
              if (item.type === 'object' && item.properties) {
                // Add two examples of the object structure
                exampleItems.push(this.buildPropertiesStructure(item.properties))
                exampleItems.push(this.buildPropertiesStructure(item.properties))
              } else {
                // Add two examples of the simple type with restrictions from the item
                exampleItems.push(this.getTypeDisplay(item.type, item.name, item.description, item))
                exampleItems.push(this.getTypeDisplay(item.type, item.name, item.description, item))
              }
            })
            structure[propName] = exampleItems
          }
          // Fallback to old itemType structure for backward compatibility
          else if (prop.itemType === 'object') {
            // Show two examples to illustrate multiple items
            structure[propName] = [
              this.buildPropertiesStructure(prop.children || []),
              this.buildPropertiesStructure(prop.children || [])
            ]
          } else {
            const itemTypeDisplay = this.getTypeDisplay(prop.itemType, prop.name, prop.description, prop)
            // Show two examples to illustrate multiple items
            structure[propName] = [itemTypeDisplay, itemTypeDisplay]
          }
        } else {
          structure[propName] = typeDisplay
        }
      })
      return structure
    },
    
    getTypeDisplay(type, name, description, property = null) {
      let typeStr = `<${type}>`
      
      // Add restriction info if available
      if (property && property.restrictionType && property.restrictionType !== 'none') {
        if (property.restrictionType === 'list' && property.restrictionValues && property.restrictionValues.length > 0) {
          const validValues = property.restrictionValues.filter(v => v && v.trim()).join(', ')
          if (validValues) {
            typeStr = `<${type}> Only [${validValues}] allowed`
          }
        } else if (property.restrictionType === 'range' && property.restrictionRange) {
          if (type === 'number' && (property.restrictionRange.min !== undefined || property.restrictionRange.max !== undefined)) {
            const min = property.restrictionRange.min !== undefined ? property.restrictionRange.min : '...'
            const max = property.restrictionRange.max !== undefined ? property.restrictionRange.max : '...'
            typeStr = `<${type}> Range: ${min} to ${max}`
          } else if (type === 'string' && (property.restrictionRange.minLength !== undefined || property.restrictionRange.maxLength !== undefined)) {
            const minLen = property.restrictionRange.minLength !== undefined ? property.restrictionRange.minLength : '0'
            const maxLen = property.restrictionRange.maxLength !== undefined ? property.restrictionRange.maxLength : '...'
            typeStr = `<${type}> Length: ${minLen}-${maxLen} chars`
          }
        } else if (property.restrictionType === 'regex' && property.restrictionPattern) {
          typeStr = `<${type}> Must match: ${property.restrictionPattern}`
        }
      }
      
      if (name && name.trim()) {
        return `${typeStr} ${name}`
      }
      return typeStr
    },
    
    onTypeChange() {
      if (this.currentObject.type === 'ARRAY') {
        this.currentObject.properties = []
        if (!this.currentObject.arrayItems) {
          this.currentObject.arrayItems = []
        }
      } else {
        this.currentObject.itemProperties = []
        this.currentObject.itemType = 'string'
        this.currentObject.arrayItems = []
      }
    },
    
    onItemTypeChange() {
      if (this.currentObject.itemType !== 'object') {
        this.currentObject.itemProperties = []
      }
    },
    
    addArrayItem() {
      if (!this.currentObject.arrayItems) {
        this.currentObject.arrayItems = []
      }
      this.currentObject.arrayItems.push({
        id: ++this.propertyIdCounter,
        name: '',
        type: 'string',
        description: '',
        properties: []
      })
      this.updateArrayItems()
    },
    
    // Helper method to normalize item types to lowercase for consistency
    normalizeItemType(item) {
      if (item && item.type) {
        item.type = item.type.toLowerCase()
      }
      return item
    },
    
    removeArrayItem(index) {
      this.currentObject.arrayItems.splice(index, 1)
      this.updateArrayItems()
    },
    
    updateArrayItems() {
      // Force reactivity
      this.currentObject = { ...this.currentObject }
      this.structureKey++
      this.$forceUpdate()
    },
    
    onArrayItemTypeChange(index) {
      const item = this.currentObject.arrayItems[index]
      if (item.type && item.type.toLowerCase() === 'object') {
        if (!item.properties) {
          this.$set(item, 'properties', [])
        }
      } else {
        this.$set(item, 'properties', [])
      }
      this.updateArrayItems()
    },
    
    addRootProperty() {
      this.currentObject.properties.push({
        id: ++this.propertyIdCounter,
        name: '',
        type: 'string',
        description: '',
        required: false,
        children: [],
        itemType: null,
        restrictionType: 'none',
        restrictionValues: [],
        restrictionRange: {},
        restrictionPattern: ''
      })
      // Force reactivity for the preview
      this.currentObject = { ...this.currentObject }
      this.$forceUpdate()
    },
    
    addArrayItemProperty(itemIndex) {
      const arrayItem = this.currentObject.arrayItems[itemIndex]
      if (!arrayItem.properties) {
        this.$set(arrayItem, 'properties', [])
      }
      arrayItem.properties.push({
        id: ++this.propertyIdCounter,
        name: '',
        type: 'string',
        description: '',
        required: false,
        children: [],
        itemType: null,
        restrictionType: 'none',
        restrictionValues: [],
        restrictionRange: {},
        restrictionPattern: ''
      })
      this.updateArrayItems()
    },
    
    updateProperty(id, property) {
      const updateInTree = (props) => {
        for (let i = 0; i < props.length; i++) {
          if (props[i].id === id) {
            props[i] = { ...props[i], ...property }
            return true
          }
          if (props[i].children && updateInTree(props[i].children)) {
            return true
          }
        }
        return false
      }
      updateInTree(this.currentObject.properties)
      // Force reactivity for the preview
      this.currentObject = { ...this.currentObject }
      this.$forceUpdate()
    },
    
    deleteProperty(id) {
      const deleteFromTree = (props) => {
        for (let i = 0; i < props.length; i++) {
          if (props[i].id === id) {
            props.splice(i, 1)
            return true
          }
          if (props[i].children && deleteFromTree(props[i].children)) {
            return true
          }
        }
        return false
      }
      deleteFromTree(this.currentObject.properties)
      // Force reactivity for the preview
      this.currentObject = { ...this.currentObject }
      this.$forceUpdate()
    },
    
    addChildProperty(parentId) {
      const addToParent = (props) => {
        for (let prop of props) {
          if (prop.id === parentId) {
            if (!prop.children) {
              prop.children = []
            }
            prop.children.push({
              id: ++this.propertyIdCounter,
              name: '',
              type: 'string',
              description: '',
              required: false,
              children: [],
              itemType: null,
              restrictionType: 'none',
              restrictionValues: [],
              restrictionRange: {},
              restrictionPattern: ''
            })
            return true
          }
          if (prop.children && addToParent(prop.children)) {
            return true
          }
        }
        return false
      }
      addToParent(this.currentObject.properties)
      // Force reactivity for the preview
      this.currentObject = { ...this.currentObject }
      this.$forceUpdate()
    },
    
    updateArrayItemProperty(itemIndex, updateData) {
      if (typeof updateData === 'object' && updateData.id && updateData.property) {
        const { id, property } = updateData
        const updateInTree = (props) => {
          for (let i = 0; i < props.length; i++) {
            if (props[i].id === id) {
              props[i] = { ...props[i], ...property }
              return true
            }
            if (props[i].children && updateInTree(props[i].children)) {
              return true
            }
          }
          return false
        }
        const arrayItem = this.currentObject.arrayItems[itemIndex]
        if (arrayItem && arrayItem.properties) {
          updateInTree(arrayItem.properties)
          this.updateArrayItems()
        }
      }
    },
    
    deleteArrayItemProperty(itemIndex, id) {
      const deleteFromTree = (props) => {
        for (let i = 0; i < props.length; i++) {
          if (props[i].id === id) {
            props.splice(i, 1)
            return true
          }
          if (props[i].children && deleteFromTree(props[i].children)) {
            return true
          }
        }
        return false
      }
      const arrayItem = this.currentObject.arrayItems[itemIndex]
      if (arrayItem && arrayItem.properties) {
        deleteFromTree(arrayItem.properties)
        this.updateArrayItems()
      }
    },
    
    addArrayItemChildProperty(itemIndex, parentId) {
      const addToParent = (props) => {
        for (let prop of props) {
          if (prop.id === parentId) {
            if (!prop.children) {
              prop.children = []
            }
            prop.children.push({
              id: ++this.propertyIdCounter,
              name: '',
              type: 'string',
              description: '',
              required: false,
              children: [],
              itemType: null,
              restrictionType: 'none',
              restrictionValues: [],
              restrictionRange: {},
              restrictionPattern: ''
            })
            return true
          }
          if (prop.children && addToParent(prop.children)) {
            return true
          }
        }
        return false
      }
      const arrayItem = this.currentObject.arrayItems[itemIndex]
      if (arrayItem && arrayItem.properties) {
        addToParent(arrayItem.properties)
        this.updateArrayItems()
      }
    },
    
    expandAll() {
      this.$emit('expand-all')
    },
    
    collapseAll() {
      this.$emit('collapse-all')
    },
    
    importPropertyStructure(propertyId, dataObjectId) {
      const selectedObject = this.dataObjects.find(obj => obj._id === dataObjectId)
      if (!selectedObject) return
      
      const updatePropertyStructure = (props) => {
        for (let prop of props) {
          if (prop.id === propertyId) {
            // Check if current property is already an array with object items
            const isArrayOfObjects = prop.type === 'array' && prop.itemType === 'object'
            
            // Import the structure based on the selected object type
            if (selectedObject.type === 'OBJECT') {
              if (isArrayOfObjects) {
                // If property is already Array of Objects, just import the structure into children
                // without changing the type from array
                this.$set(prop, 'children', JSON.parse(JSON.stringify(selectedObject.properties || [])))
              } else {
                // For non-array properties or arrays with non-object items, update to object type
                this.$set(prop, 'children', JSON.parse(JSON.stringify(selectedObject.properties || [])))
                if (prop.type !== 'object') {
                  this.$set(prop, 'type', 'object')
                }
              }
            } else if (selectedObject.type === 'ARRAY') {
              this.$set(prop, 'itemType', selectedObject.itemType || 'string')
              if (selectedObject.itemType === 'object') {
                this.$set(prop, 'children', JSON.parse(JSON.stringify(selectedObject.itemProperties || [])))
              }
              // Update the property type to array to ensure proper handling
              if (prop.type !== 'array') {
                this.$set(prop, 'type', 'array')
              }
            }
            this.assignPropertyIds(prop.children || [])
            return true
          }
          if (prop.children && updatePropertyStructure(prop.children)) {
            return true
          }
        }
        return false
      }
      
      const updated = updatePropertyStructure(this.currentObject.properties)
      if (updated) {
        // Force reactivity by creating a new reference and updating the structure key
        this.currentObject = { ...this.currentObject }
        this.structureKey++
        this.$forceUpdate()
        this.$message.success(`Imported structure from "${selectedObject.name}"`)
      } else {
        this.$message.error('Failed to find property to import into')
      }
    },
    
    importArrayItemPropertyStructure(itemIndex, propertyId, dataObjectId) {
      const selectedObject = this.dataObjects.find(obj => obj._id === dataObjectId)
      if (!selectedObject) return
      
      const arrayItem = this.currentObject.arrayItems[itemIndex]
      if (!arrayItem || !arrayItem.properties) return
      
      const updatePropertyStructure = (props) => {
        for (let prop of props) {
          if (prop.id === propertyId) {
            // Check if current property is already an array with object items
            const isArrayOfObjects = prop.type === 'array' && prop.itemType === 'object'
            
            // Import the structure based on the selected object type
            if (selectedObject.type === 'OBJECT') {
              if (isArrayOfObjects) {
                // If property is already Array of Objects, just import the structure into children
                // without changing the type from array
                this.$set(prop, 'children', JSON.parse(JSON.stringify(selectedObject.properties || [])))
              } else {
                // For non-array properties or arrays with non-object items, update to object type
                this.$set(prop, 'children', JSON.parse(JSON.stringify(selectedObject.properties || [])))
                if (prop.type !== 'object') {
                  this.$set(prop, 'type', 'object')
                }
              }
            } else if (selectedObject.type === 'ARRAY') {
              this.$set(prop, 'itemType', selectedObject.itemType || 'string')
              if (selectedObject.itemType === 'object') {
                this.$set(prop, 'children', JSON.parse(JSON.stringify(selectedObject.itemProperties || [])))
              }
              // Update the property type to array to ensure proper handling
              if (prop.type !== 'array') {
                this.$set(prop, 'type', 'array')
              }
            }
            this.assignPropertyIds(prop.children || [])
            return true
          }
          if (prop.children && updatePropertyStructure(prop.children)) {
            return true
          }
        }
        return false
      }
      
      const updated = updatePropertyStructure(arrayItem.properties)
      if (updated) {
        // Force reactivity by creating a new reference and updating the structure key
        this.updateArrayItems()
        this.$message.success(`Imported structure from "${selectedObject.name}"`)
      } else {
        this.$message.error('Failed to find property to import into')
      }
    },
    
    confirmArrayImport() {
      if (!this.selectedArrayImportObject) return
      
      const selectedObj = this.dataObjects.find(obj => obj._id === this.selectedArrayImportObject)
      if (!selectedObj) return
      
      this.$confirm(
        `Import structure from "${selectedObj.name}"? This will replace the current array item structure.`,
        'Confirm Import',
        {
          confirmButtonText: 'Import',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      ).then(() => {
        // Import the structure from the selected object
        if (selectedObj.type === 'OBJECT' && selectedObj.properties) {
          this.currentObject.itemProperties = JSON.parse(JSON.stringify(selectedObj.properties))
          this.assignPropertyIds(this.currentObject.itemProperties)
          // Force reactivity
          this.currentObject = { ...this.currentObject }
          this.structureKey++
          this.$forceUpdate()
          this.$message.success(`Imported structure from "${selectedObj.name}"`)
        }
        this.selectedArrayImportObject = null
      }).catch(() => {
        this.selectedArrayImportObject = null
      })
    },
    
    showArrayItemImportDialog(itemIndex) {
      this.currentArrayItemIndex = itemIndex
      this.selectedArrayItemImportObject = null
      this.arrayItemImportDialogVisible = true
    },
    
    cancelArrayItemImport() {
      this.arrayItemImportDialogVisible = false
      this.selectedArrayItemImportObject = null
      this.currentArrayItemIndex = null
    },
    
    confirmArrayItemImportDialog() {
      if (!this.selectedArrayItemImportObject || this.currentArrayItemIndex === null) return
      
      const selectedObj = this.dataObjects.find(obj => obj._id === this.selectedArrayItemImportObject)
      if (!selectedObj) return
      
      // Import the structure from the selected object
      const arrayItem = this.currentObject.arrayItems[this.currentArrayItemIndex]
      if (selectedObj.type === 'OBJECT' && selectedObj.properties && arrayItem) {
        this.$set(arrayItem, 'properties', JSON.parse(JSON.stringify(selectedObj.properties)))
        this.assignPropertyIds(arrayItem.properties)
        // Ensure the array item type remains as object
        this.$set(arrayItem, 'type', 'object')
        this.updateArrayItems()
        this.$message.success(`Imported structure from "${selectedObj.name}"`)
      }
      
      // Close dialog and reset
      this.arrayItemImportDialogVisible = false
      this.selectedArrayItemImportObject = null
      this.currentArrayItemIndex = null
    },
    
    confirmArrayItemImport(itemIndex) {
      if (!this.selectedArrayItemImportObject) return
      
      const selectedObj = this.dataObjects.find(obj => obj._id === this.selectedArrayItemImportObject)
      if (!selectedObj) return
      
      this.$confirm(
        `Import structure from "${selectedObj.name}"? This will replace the current element structure.`,
        'Confirm Import',
        {
          confirmButtonText: 'Import',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      ).then(() => {
        // Import the structure from the selected object
        const arrayItem = this.currentObject.arrayItems[itemIndex]
        if (selectedObj.type === 'OBJECT' && selectedObj.properties && arrayItem) {
          this.$set(arrayItem, 'properties', JSON.parse(JSON.stringify(selectedObj.properties)))
          this.assignPropertyIds(arrayItem.properties)
          // Update array item type to object
          this.$set(arrayItem, 'type', 'object')
          this.updateArrayItems()
          this.$message.success(`Imported structure from "${selectedObj.name}"`)
        }
        this.selectedArrayItemImportObject = null
      }).catch(() => {
        this.selectedArrayItemImportObject = null
      })
    }
  }
}
</script>

<style scoped>
.data-object-management {
  padding: 20px;
}

.management-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.management-header h2 {
  margin: 0;
  color: #333;
  font-weight: 500;
}
.header-actions {
  display: flex;
  gap: 10px;
}

.data-object-list {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.object-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.object-name i {
  color: #409EFF;
}

.structure-editor {
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  padding: 15px;
  background: #f5f7fa;
  min-height: 300px;
}

.structure-toolbar {
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #DCDFE6;
}

.structure-tree {
  max-height: 400px;
  overflow-y: auto;
}

.array-items-editor {
  padding: 10px;
  background: white;
  border-radius: 4px;
}

.array-item-type {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.array-item-type label {
  font-weight: 500;
  color: #606266;
}

.array-object-properties {
  margin-top: 15px;
  padding: 15px;
  background: #f5f7fa;
  border-radius: 4px;
}

.array-object-properties h4 {
  margin: 0 0 10px 0;
  color: #606266;
}

.array-object-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
}

.array-object-header h4 {
  margin: 0;
}

.import-panel {
  padding: 15px;
}

.import-panel h4 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.import-description {
  margin: 0 0 15px 0;
  color: #606266;
  font-size: 13px;
  line-height: 1.4;
}

.import-note {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #E6A23C;
  font-size: 12px;
  background: #FDF6EC;
  padding: 8px;
  border-radius: 4px;
  border-left: 3px solid #E6A23C;
}

.import-button {
  background: linear-gradient(135deg, #409EFF 0%, #36A3F7 100%);
  border: none;
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(64, 158, 255, 0.2);
}

.import-button:hover {
  background: linear-gradient(135deg, #66B1FF 0%, #409EFF 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(64, 158, 255, 0.3);
}

.structure-preview {
  background: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
  overflow-x: auto;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.5;
}

.array-items-header {
  margin-bottom: 15px;
}

.array-items-header h4 {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.array-description {
  margin: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.array-items-list {
  margin-bottom: 20px;
}

.array-item-card {
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.array-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.item-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.item-index {
  background: #409EFF;
  color: white;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 12px;
  min-width: 28px;
  text-align: center;
}

.item-name {
  width: 200px;
}

.item-type {
  width: 120px;
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-description {
  margin-bottom: 12px;
}

.array-object-properties {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  border: 1px solid #e9ecef;
}

.object-properties-header {
  margin-bottom: 10px;
}

.object-properties-header h5 {
  margin: 0;
  color: #495057;
  font-size: 14px;
  font-weight: 600;
}

.no-array-items {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
  background: #f8f9fa;
  border-radius: 6px;
  border: 2px dashed #dee2e6;
}

.no-array-items p {
  margin: 0;
  font-size: 14px;
}

.array-actions {
  text-align: center;
  padding-top: 15px;
  border-top: 1px solid #e4e7ed;
}

.dialog-content-layout {
  display: flex;
  gap: 20px;
  height: 70vh;
}

.left-column {
  flex: 1;
  overflow-y: auto;
}

.right-column {
  width: 400px;
  border-left: 1px solid #DCDFE6;
  padding-left: 20px;
}

.structure-preview-section {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.structure-preview-section h3 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
  padding-bottom: 10px;
  border-bottom: 1px solid #DCDFE6;
}

.preview-container {
  flex: 1;
  overflow: hidden;
  border-radius: 6px;
  border: 1px solid #DCDFE6;
}

.json-preview {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  overflow: auto;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.4;
  color: #333;
  height: 100%;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.import-dialog-content {
  padding: 10px;
}

.import-dialog-description {
  margin: 0 0 20px 0;
  color: #606266;
  font-size: 14px;
}

.import-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding: 12px;
  background: #FDF6EC;
  border-radius: 4px;
  border-left: 3px solid #E6A23C;
  color: #E6A23C;
  font-size: 13px;
}

.import-warning i {
  font-size: 16px;
}

.import-from-button {
  margin-right: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.import-from-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}
</style>